//REACT AND REACT BASED LIBRARIES
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
//COMPONENTS
import { Box, Container } from "@mui/material";
import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ColumnInterface } from "@components/Table/types";
//CUSTOM HOOKS AND UTILS
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { useFetchMock } from "src/mock_data/useMockData";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { useFetchTable } from "@hooks/useFetchDataTable";
import dayjs from "dayjs";

const ChangeStatusCompletion: React.FC = () => {
  const { t } = useTranslation(["overall-page"]);
  const { t: tOptions } = useTranslation(["input"]);
  const navigate = useNavigate();
  const {
    id,
    idUser,
    activityType,
    idActivity,
    learningObjectId,
    grandparentActivityId,
    parentActivityId,
    idSCORM,
  } = useParams();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const [emptyData, setEmptyData] = useState(false);

  const getPageID = () => {
    switch (activityType) {
      case "DA_SCORM":
        return "corporate.users.overall.scorm.status";
      case "DA_VIRTUAL_CLASS":
      case "DA_PHYSICAL_CLASS":
        return "corporate.users.overall.custom.status";
      default:
        return "corporate.users.overall.xapi.status";
    }
  };

  const getEndpointGETURL = () => {
    switch (activityType) {
      case "DA_SCORM":
        return `${ServicesURL.scorm}?corporateId=${id}&studentId=${idUser}&activityId=${idActivity}&learningObjectId=${learningObjectId}&parentActivityId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentActivityId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
      case "DA_VIRTUAL_CLASS":
      case "DA_PHYSICAL_CLASS":
        return `${ServicesURL.customTracking}?corporateId=${id}&activityId=${idActivity}&studentId=${idUser}&parentId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
      default:
        return `${ServicesURL.xapi}?corporateId=${id}&studentId=${idUser}&activityId=${idActivity}&isBrightcove=${activityType === "DA_EXTERNAL_RES"}&parentActivityId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentActivityId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
    }
  };

  const getEndpointPATCHURL = () => {
    switch (activityType) {
      case "DA_SCORM":
        return `${ServicesURL.scorm}?corporateId=${id}&studentId=${idUser}&activityId=${idActivity}&learningObjectId=${learningObjectId}&parentActivityId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentActivityId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
      case "DA_VIRTUAL_CLASS":
      case "DA_PHYSICAL_CLASS":
        return `${ServicesURL.customTracking}?corporateId=${id}&activityId=${idActivity}&studentId=${idUser}&learningObjectId=${learningObjectId}&parentActivityId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentActivityId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
      default:
        return `${ServicesURL.xapi}?corporateId=${id}&studentId=${idUser}&activityId=${idActivity}&completedInsteadOfPassed=${true}&isBrightcove=${activityType === "DA_EXTERNAL_RES"}&parentActivityId=${parentActivityId !== "null" ? parentActivityId : ""}&grandparentActivityId=${grandparentActivityId !== "null" ? grandparentActivityId : ""}`;
    }
  };

  const { columns, dataTable, mutate } = useFetchTable(
    getEndpointGETURL(),
    PagesID[getPageID()],
  );

  useEffect(() => {
    if (!dataTable) return;
    switch (activityType) {
      case "DA_SCORM":
        if (dataTable.length === 0) {
          setEmptyData(true);
        }
        break;
      default:
        if (!dataTable?.currentStatus) {
          setEmptyData(true);
        }
        break;
    }
  }, [dataTable]);

  const { trigger: patchChangeStatus } = useSWRMutation(
    getEndpointPATCHURL(),
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.PATCH, { ...arg.arg });
    },
  );

  const { trigger: patchXapi } = useSWRMutation(
    getEndpointPATCHURL(),
    (url: string, { arg }: { arg: { obj: any; passed: boolean } }) => {
      return fetcher<any>(
        `${url}&completedInsteadOfPassed=${arg.passed}`,
        MethodType.PATCH,
        { ...arg.obj },
      );
    },
  );

  const ObjToSendBasedOnType = (inputForm: any) => {
    const GETobjFromBE: any = () => {
      switch (activityType) {
        case "DA_SCORM":
          if (idSCORM) {
            return dataTable[idSCORM];
          } else {
            return dataTable[0];
          }
        default:
          return dataTable;
      }
    };

    let objFromBE = GETobjFromBE();

    switch (activityType) {
      case "DA_SCORM":
        return {
          currentStatus: inputForm.currentStatus,
          newStatus: inputForm.newStatus,
          lessonLocationValue: inputForm.lessonLocationValue,
          lessonLocationBinding: objFromBE.lessonLocationBinding,
          lessonLocationDataModelId: objFromBE.lessonLocationDataModelId,
          scoreValue: inputForm.scoreValue,
          scoreBinding: objFromBE.scoreBinding,
          scoreDataModelId: objFromBE.scoreDataModelId,
          reason: inputForm.reason,
          completionDate: inputForm?.completionDate
            ? dayjs(inputForm?.completionDate).format("YYYY-MM-DD")
            : objFromBE?.completionDate,
          suspendDateValue: inputForm.suspendDateValue,
          suspendDateBinding: objFromBE.suspendDateBinding,
          suspendDateDataModelId: objFromBE.suspendDateDataModelId,
          statusDataModelId: objFromBE.statusDataModelId,
          statusBinding: objFromBE.statusBinding,
          scormType: objFromBE.scormType,
        };
      case "DA_VIRTUAL_CLASS":
      case "DA_PHYSICAL_CLASS":
        return {
          currentStatus: inputForm.currentStatus,
          newStatus: inputForm.newStatus,
          score: inputForm.score,
          reason: inputForm.reason,
          completionDate: inputForm?.completionDate
            ? dayjs(inputForm?.completionDate).format("YYYY-MM-DD")
            : objFromBE?.completionDate,
        };
      default:
        if (inputForm.state !== objFromBE.state) {
          return {
            currentStatus: inputForm.currentStatus,
            newStatus: inputForm.newStatus,
            state: inputForm.state,
            score: inputForm.score,
            reason: inputForm.reason,
            completionDate: inputForm?.completionDate
              ? dayjs(inputForm?.completionDate).format("YYYY-MM-DD")
              : objFromBE?.completionDate,
            statementId: objFromBE.statementId,
          };
        } else {
          return {
            currentStatus: inputForm.currentStatus,
            newStatus: inputForm.newStatus,
            score: inputForm.score,
            reason: inputForm.reason,
            completionDate: inputForm?.completionDate
              ? dayjs(inputForm?.completionDate).format("YYYY-MM-DD")
              : objFromBE?.completionDate,
            statementId: objFromBE.statementId,
          };
        }
    }
  };

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    let objToSend = ObjToSendBasedOnType(inputForm);
    try {
      switch (activityType) {
        case "DA_SCORM":
        case "DA_VIRTUAL_CLASS":
        case "DA_PHYSICAL_CLASS":
          await patchChangeStatus(objToSend);
          break;
        default:
          await patchXapi({
            obj: objToSend,
            passed: inputForm?.completedInsteadOfPassed || false,
          });
          break;
      }
      mutate && mutate();
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  let inputsException = (col: any, inputForm: any) => {
    if (col.accessorKey === "newStatus") {
      if (inputForm.newStatus === "P") {
        inputForm.completedInsteadOfPassed = null;
      }
    }

    if (col.accessorKey === "completedInsteadOfPassed") {
      if (inputForm.newStatus === "C") {
        col.disabled = false;
        col.required = true;
      }
    }
    if (col.accessorKey === "completionDate") {
      if (
        ["P", "E"].includes(inputForm.currentStatus) &&
        inputForm.newStatus === "C"
      ) {
        col.required = true;
      } else {
        col.required = false;
      }

      if (
        (inputForm?.newStatus && inputForm.newStatus === "C") ||
        (!inputForm?.newStatus && inputForm.currentStatus === "C")
      ) {
        col.disabled = false;
      } else {
        col.disabled = true;
      }
    }
    if (col.accessorKey === "scoreValue" || col.accessorKey === "score") {
      if (
        ["P", "E"].includes(inputForm.currentStatus) &&
        inputForm.newStatus === "C"
      ) {
        col.disabled = false;
      } else {
        col.disabled = true;
      }
    }
    return col;
  };

  const handleDataRow = useMemo(() => {
    if (dataTable) {
      switch (activityType) {
        case "DA_SCORM":
          if (idSCORM) {
            return { ...dataTable[idSCORM] };
          }
          if (dataTable[0]?.currentStatus) {
            return { ...dataTable[0] };
          }
          return { ...dataTable[0] };
        default:
          if (dataTable?.currentStatus) {
            return { ...dataTable };
          }
          return { ...dataTable };
      }
    } else {
      return {};
    }
  }, [dataTable]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("change-status.overall.hero-title")} - ${tOptions(`options.activity_type.${(activityType || "").toLowerCase()}`)}`}
          description={t("change-status.overall.hero-description")}
        />
        {columns && handleDataRow && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <AddEditFormCst
              row={handleDataRow}
              columns={columns}
              onSubmit={onSubmit}
              actionsForForms={[]}
              inputsException={inputsException}
              backButton={() => {
                navigate(-1);
              }}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
      <ModalConfirmsCst
        open={emptyData}
        title={""}
        description={""}
        onCallBackClosed={() => {
          navigate(-1);
        }}
        status={{
          s: StatusEnum.KO,
          message: "",
          title: "Resource is not available",
        }}
      />
    </AnimationFadeIn>
  );
};

export default ChangeStatusCompletion;
